import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, Lock, Loader2, RefreshCw, Eye } from "lucide-react";
import Header from "../components/Header";
import { adminLogin, fetchFeedback, fetchStats, API } from "../lib/api";
import AdminStats from "../components/AdminStats";
import StudentProfileDrawer from "../components/StudentProfileDrawer";

const TOKEN_KEY = "atmia_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async (t) => {
    setLoading(true);
    try {
      const [data, st] = await Promise.all([fetchFeedback(t), fetchStats(t)]);
      setItems(data.items || []);
      setStats(st);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to load feedback.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    try {
      const res = await adminLogin(password);
      sessionStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
      toast.success("Welcome, admin");
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems([]);
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/feedback/csv`, { headers: { "X-Admin-Password": token } });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `atmia_feedback_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV download failed");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-sand-50">
        <Header minimal />
        <div className="max-w-md mx-auto px-6 py-20">
          <div className="bg-white border border-stone-200 rounded-2xl p-8" data-testid="admin-login-card">
            <div className="w-12 h-12 rounded-xl bg-saffron-100 text-saffron-600 flex items-center justify-center">
              <Lock className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <h1 className="font-serif text-3xl mt-5 text-stone-900">Admin Login</h1>
            <p className="text-stone-600 mt-2 text-sm">Enter the admin password to view feedback responses.</p>
            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <input
                type="password"
                placeholder="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-500/15"
                autoFocus
              />
              <button
                type="submit" disabled={submitting}
                data-testid="admin-login-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white rounded-full px-6 py-3 font-medium disabled:opacity-70"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Sign in
              </button>
              <Link to="/" data-testid="admin-back-home" className="block text-center text-sm text-stone-500 hover:text-saffron-600">
                ← Back to site
              </Link>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <Header minimal />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4">
          <div>
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-saffron-600 font-medium">Admin Dashboard</div>
            <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl mt-1.5 sm:mt-2 text-stone-900">Feedback Responses</h1>
            <p className="text-stone-600 mt-1 text-xs sm:text-sm" data-testid="admin-count-label">
              {items.length} response{items.length === 1 ? "" : "s"} collected
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button onClick={() => load(token)} data-testid="admin-refresh-button"
              className="inline-flex items-center gap-1.5 sm:gap-2 bg-white border border-stone-200 hover:bg-stone-50 text-stone-700 rounded-full px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium">
              <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={downloadCsv} data-testid="admin-download-csv"
              className="inline-flex items-center gap-1.5 sm:gap-2 bg-saffron-500 hover:bg-saffron-600 text-white rounded-full px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium shadow-sm btn-shine">
              <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden xs:inline">Download </span>CSV
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 sm:gap-2 text-stone-600 hover:text-saffron-600 text-xs sm:text-sm font-medium px-2 sm:px-3 py-2">
              <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8">
          <AdminStats stats={stats} />
        </div>

        <div className="mt-6 bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-feedback-table">
              <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Date</th>
                  <th className="text-left px-5 py-3 font-medium">Parent/Student</th>
                  <th className="text-left px-5 py-3 font-medium">Student</th>
                  <th className="text-left px-5 py-3 font-medium">Batch</th>
                  <th className="text-left px-5 py-3 font-medium">City/Branch</th>
                  <th className="text-left px-5 py-3 font-medium">Rating</th>
                  <th className="text-left px-5 py-3 font-medium">Contact</th>
                  <th className="text-right px-5 py-3 font-medium">View</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading && (
                  <tr><td colSpan={8} className="text-center py-12 text-stone-500">Loading...</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-stone-500" data-testid="admin-empty-row">No feedback yet.</td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id} className="hover:bg-stone-50" data-testid={`admin-row-${it.id}`}>
                    <td className="px-5 py-3 text-stone-600">{(it.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-3 font-medium text-stone-900">{it.parent_name}</td>
                    <td className="px-5 py-3 text-stone-700">{it.student_name}</td>
                    <td className="px-5 py-3 text-stone-700">{it.batch_year}</td>
                    <td className="px-5 py-3 text-stone-700">{it.city_branch}</td>
                    <td className="px-5 py-3">
                      <RatingBadge value={it.answers?.q40} />
                    </td>
                    <td className="px-5 py-3 text-stone-600">{it.contact_number}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setSelected(it)} data-testid={`admin-view-${it.id}`}
                        className="inline-flex items-center gap-1 text-saffron-600 hover:text-saffron-700">
                        <Eye className="w-4 h-4" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && <StudentProfileDrawer item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function RatingBadge({ value }) {
  if (!value) return <span className="text-stone-400 text-xs">—</span>;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-saffron-100 text-saffron-700 text-xs font-medium">
      {value}★
    </span>
  );
}
